import {Component, ElementRef, forwardRef, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AppContext} from "../../../../../app-context";
import {AbstractValueAccessorComponent} from "../../../../../common/component/value-accessor.component";
import {CargoDescription, LocalMovement} from "@portbase/bezoekschip-service-typescriptmodels";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {CollapseComponent} from "../../../../../common/collapse/collapse.component";
import {VisitContext} from "../../../../visit-context";

@Component({
    selector: 'app-storm-pilotage',
    templateUrl: './storm-pilotage.component.html',
    styleUrls: ['./storm-pilotage.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => StormPilotageComponent), multi: true }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StormPilotageComponent extends AbstractValueAccessorComponent<LocalMovement> implements OnInit {

	appContext = AppContext;
	context = VisitContext;
  maximumTankerLengthLOA : number = 125;

	@Input() localMovement: LocalMovement;
	@Input() vesselLength: number;
	@Input() isTanker: boolean;

	@Input() firstMovement: boolean;
	@Input() required;
	@Input() id: string;

	@ViewChild('stormPilotageCollapse') stormPilotageCollapse: CollapseComponent;

	hoistingOrLanding = value => value ? "Hoisting" : "Landing";

	get showHeliQuestions(): boolean {
		return this.vesselLength >= 125;
	}

	get showLoaQuestions(): boolean {
    const isLoaApplicable = this.vesselLength <= this.maxLengthForLoa && this.vesselDraftLessThan10Meters;

    return isLoaApplicable && (this.isTanker ? this.isLoaApplicableTanker : true);
	}

	get isLoaRequired(): boolean {
		return !!this.localMovement.order && this.required && ((this.appContext.stormPilotageRotterdam === 'HALTED_FOR_ALL'
        && this.vesselLength <= this.maxLengthForLoa && this.vesselDraftLessThan10Meters)
      || (this.appContext.stormPilotageRotterdam === 'HALTED_FOR_SMALL'
        && this.vesselLength < 125 && this.vesselDraftLessThan10Meters));
	}

  get hasPECHolder(): boolean {
    return this.localMovement.pilotExemption && !!this.localMovement.pilotExemption.holderName;
  }

  get falseNotAllowedForFirstRemotePilotage(): boolean {
    let firstBerthVisitAta = this.context.visit.visitDeclaration.portVisit.berthVisits[0].ata

    return !firstBerthVisitAta && this.isLoaRequired && !this.hasPECHolder
      && this.localMovement.stormPilotageInformation?.remotePilotage === false && this.firstMovement;
  }

  get falseNotAllowedForLastRemotePilotage(): boolean {
    return this.isLoaRequired && !this.hasPECHolder
      && this.localMovement.stormPilotageInformation?.remotePilotage === false
      && this.context.isLastMovement(this.localMovement);
  }

  get isLoaApplicableTanker() : boolean {
    return this.isTanker
      && (this.firstMovement || this.context.isLastMovement(this.localMovement))
      && (this.vesselLength < this.maximumTankerLengthLOA
      || this.localMovement.cargo == CargoDescription.BALLAST_CONDITION);
  }

	constructor(private elementRef: ElementRef) {
		super();
	}

	get maxLengthForLoa(): number {
		const inboundThreshold = 160;
		const outboundThreshold = 160;
		return this.firstMovement ? inboundThreshold : outboundThreshold;
	}

	ngOnInit(): void {
		this.ensureStormPilotageInformation();
	}

	get vesselDraftLessThan10Meters(): boolean {
		return this.localMovement.vesselDraft <= 10;
	}

	get value(): LocalMovement {
		return this.localMovement;
	}

	writeValue(value: LocalMovement): void {
		this.localMovement = value;
		this.ensureStormPilotageInformation();
	};

	private ensureStormPilotageInformation(): void {
		if (this.localMovement && !this.localMovement.stormPilotageInformation) {
			this.localMovement.stormPilotageInformation = {
				heliSuitable: null,
				remotePilotage: null,
				imoLoaRegistered: null,
				lowFreeboard: null,
				hoisting: null,
				remarks: null
			};
		}
	}

}
